/**
 * Utilitários de Analytics e Tracking
 * Enhanced E-commerce Tracking para Google Analytics
 */

// Verificar se gtag está disponível
const isGtagAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Inicializa analytics (se necessário)
 */
export const initializeAnalytics = () => {
  // Analytics já é inicializado no index.html
  // Esta função pode ser usada para configurações adicionais
  if (isGtagAvailable()) {
    // Habilitar Enhanced E-commerce
    window.gtag('config', 'G-HGYW2Q9403', {
      send_page_view: true
    });
  }
};

/**
 * Track page view
 */
export const trackPageView = (path) => {
  if (!isGtagAvailable()) return;

  window.gtag('config', 'G-HGYW2Q9403', {
    page_path: path
  });
};

/**
 * Track view de produto
 */
export const trackProductView = (product) => {
  if (!isGtagAvailable() || !product) return;

  window.gtag('event', 'view_item', {
    currency: 'BRL',
    value: product.price,
    items: [{
      item_id: product._id,
      item_name: product.name,
      item_category: product.category?.name || 'Produtos',
      item_brand: product.brand || 'Emperc',
      price: product.price,
      quantity: 1
    }]
  });
};

/**
 * Track adição ao carrinho
 */
export const trackAddToCart = (product, quantity = 1) => {
  if (!isGtagAvailable() || !product) return;

  window.gtag('event', 'add_to_cart', {
    currency: 'BRL',
    value: product.price * quantity,
    items: [{
      item_id: product._id,
      item_name: product.name,
      item_category: product.category?.name || 'Produtos',
      item_brand: product.brand || 'Emperc',
      price: product.price,
      quantity: quantity
    }]
  });
};

/**
 * Track remoção do carrinho
 */
export const trackRemoveFromCart = (product, quantity = 1) => {
  if (!isGtagAvailable() || !product) return;

  window.gtag('event', 'remove_from_cart', {
    currency: 'BRL',
    value: product.price * quantity,
    items: [{
      item_id: product._id,
      item_name: product.name,
      item_category: product.category?.name || 'Produtos',
      item_brand: product.brand || 'Emperc',
      price: product.price,
      quantity: quantity
    }]
  });
};

/**
 * Track início do checkout
 */
export const trackBeginCheckout = (items, totalValue) => {
  if (!isGtagAvailable() || !items || items.length === 0) return;

  window.gtag('event', 'begin_checkout', {
    currency: 'BRL',
    value: totalValue,
    items: items.map(item => ({
      item_id: item.product?._id || item.productId || item._id,
      item_name: item.name || item.product?.name,
      item_category: item.category?.name || item.product?.category?.name || 'Produtos',
      item_brand: item.brand || item.product?.brand || 'Emperc',
      price: item.price || item.product?.price,
      quantity: item.quantity || 1
    }))
  });
};

/**
 * Track compra concluída
 */
export const trackPurchase = (order) => {
  if (!isGtagAvailable() || !order) return;

  const items = order.items || [];
  const transactionId = order.externalReference || order._id;

  window.gtag('event', 'purchase', {
    transaction_id: transactionId,
    value: order.totalPrice || order.totalAmount,
    currency: 'BRL',
    shipping: order.shippingCost || 0,
    payment_type: order.paymentMethod || 'unknown',
    items: items.map(item => ({
      item_id: item.product?._id || item.productId || item._id,
      item_name: item.name || item.product?.name,
      item_category: item.category?.name || item.product?.category?.name || 'Produtos',
      item_brand: item.brand || item.product?.brand || 'Emperc',
      price: item.price || item.product?.price,
      quantity: item.quantity || 1
    }))
  });
};

/**
 * Track compartilhamento social
 */
export const trackSocialShare = (platform, contentType, contentId, contentUrl) => {
  if (!isGtagAvailable()) return;

  window.gtag('event', 'share', {
    method: platform, // 'WhatsApp', 'Facebook', 'Twitter', etc.
    content_type: contentType, // 'product', 'category', 'page'
    content_id: contentId,
    content_url: contentUrl
  });
};

/**
 * Track busca
 */
export const trackSearch = (searchTerm, resultsCount = 0) => {
  if (!isGtagAvailable()) return;

  window.gtag('event', 'search', {
    search_term: searchTerm,
    results_count: resultsCount
  });
};

/**
 * Track visualização de categoria
 */
export const trackViewCategory = (categoryName, productsCount = 0) => {
  if (!isGtagAvailable()) return;

  window.gtag('event', 'view_item_list', {
    item_list_name: categoryName,
    item_list_id: categoryName.toLowerCase().replace(/\s+/g, '_'),
    items: [] // Pode ser preenchido com produtos da categoria
  });
};

/**
 * Track adição à wishlist
 */
export const trackAddToWishlist = (product) => {
  if (!isGtagAvailable() || !product) return;

  window.gtag('event', 'add_to_wishlist', {
    currency: 'BRL',
    value: product.price,
    items: [{
      item_id: product._id,
      item_name: product.name,
      item_category: product.category?.name || 'Produtos',
      item_brand: product.brand || 'Emperc',
      price: product.price
    }]
  });
};

/**
 * Track custom event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (!isGtagAvailable()) return;

  window.gtag('event', eventName, eventParams);
};
